import { LayoutElementType } from '@app-store'
import { ROWS_TAB_DIFFERENCE } from '../constants/form.constants'
import { TabIndexMap } from '../types/common'
import { Column, EntityLayout, Page } from '../types/layout.types'

export const generateTabIndexMap = (
  pages: Page[],
  baseOffset = 0
): TabIndexMap => {
  const tabIndexMap: TabIndexMap = {}

  pages.forEach((page: Page) => {
    const pageId = page.pageId
    tabIndexMap[pageId] = {}

    let currentTabIndexOffset = baseOffset
    let previousRowWasSubform = false
    page?.rows?.forEach((row, index) => {
      const rowId = row.rowId
      const columns = row.columns || []

      const columnsElements = columns.map((col) => col.elements || [])
      const maxLength = Math.max(...columnsElements.map((e) => e.length))
      const tabIndexObj: Record<string, number> = {}

      let localTabIndex = 0

      if (row.label) {
        const labelKey = `${rowId}-${row.label}`?.replace(' ', '')
        tabIndexObj[labelKey] = localTabIndex++
      }

      for (let i = 0; i < maxLength; i++) {
         for (const column of columnsElements) {
          const element = column[i]
          if (element?.elementId) {
            tabIndexObj[element.elementId] = localTabIndex++
          }
        }
      
      }
      if (previousRowWasSubform) {
        currentTabIndexOffset += 500
      }
      tabIndexMap[pageId][rowId] = {
        tabIndex: tabIndexObj,
        tabIndexOffset:
          currentTabIndexOffset + (index + 1) * ROWS_TAB_DIFFERENCE,
      }

      currentTabIndexOffset += localTabIndex

      previousRowWasSubform = row.rowType === 'sub-form'
    })
  })

  return tabIndexMap
}

const getColumnElements = (columns: Column[]) =>
  columns.map((col) => col.elements || [])
const getColumnsMaxLength = (columnsElements: LayoutElementType[][]) =>
  Math.max(...columnsElements.map((e) => e.length))

export const generateTabIndexMapForSubForms = (
  layouts: Array<EntityLayout & { layoutId: string }>,
  baseOffset = 0
): Record<string, TabIndexMap> => {
  const layoutTabIndexMap: Record<string, TabIndexMap> = {}
  let currentTabIndexOffset = baseOffset

  layouts.forEach((layout) => {
    const layoutId = layout.layoutId
    const pages = layout.layout_data?.pages || []

    const tabIndexMap: TabIndexMap = {}

    pages.forEach((page: Page) => {
      const pageId = page.pageId
      tabIndexMap[pageId] = {}

      let previousRowWasSubform = false

      page?.rows?.forEach((row, index) => {
        const rowId = row.rowId
        const columns = row.columns || []
        const columnsElements = getColumnElements(columns)
        const maxLength = getColumnsMaxLength(columnsElements)
        const tabIndexObj: Record<string, number> = {}

        let localTabIndex = 0

        if (row.label) {
          const labelKey = `${rowId}-${row.label}`.replace(/ /g, '')
          tabIndexObj[labelKey] = localTabIndex++
        }

        for (let i = 0; i < maxLength; i++) {
           for (const columnElements of columnsElements) {
            const element = columnElements[i]
            if (element?.elementId) {
              tabIndexObj[element.elementId] = localTabIndex++
            }
          
          }
        }

        if (previousRowWasSubform) {
          currentTabIndexOffset += 500
        }

        tabIndexMap[pageId][rowId] = {
          tabIndex: tabIndexObj,
          tabIndexOffset:
            currentTabIndexOffset + (index + 1) * ROWS_TAB_DIFFERENCE,
        }

        currentTabIndexOffset += localTabIndex
        previousRowWasSubform = row.rowType === 'sub-form'
      })
    })

    layoutTabIndexMap[layoutId] = tabIndexMap
  })

  return layoutTabIndexMap
}
export const getMaxTabIndexValue = (
  layoutTabIndexMap: Record<string, TabIndexMap>
): number => {
  let maxValue = 0

  for (const layoutId in layoutTabIndexMap) {
    const layout = layoutTabIndexMap[layoutId]
    for (const pageId in layout) {
      const page = layout[pageId]
      for (const rowId in page) {
        const row = page[rowId]
        const offset = row?.tabIndexOffset ?? 0

        const tabIndexes = Object.values(row.tabIndex || {})
        const localMax = tabIndexes.length ? Math.max(...tabIndexes) : 0

        const total = offset + localMax
        if (total > maxValue) {
          maxValue = total
        }
      }
    }
  }

  return maxValue
}

export const getMinTabIndexForPageLevel = (data: TabIndexMap) => {
  let max = Infinity

  for (const page of Object.values(data)) {
    for (const row of Object.values(page)) {
      const { tabIndex, tabIndexOffset } = row
      for (const index of Object.values(tabIndex)) {
        const combined = tabIndexOffset + index
        if (combined < max) {
          max = combined
        }
      }
    }
  }

  return !isFinite(max) ? 0 : max
}
